/* Change Password tab , current + new + confirm fields → POST /admin/change-password. */
import React, { useState } from "react";
import { KeyRound, Lock, Check } from "lucide-react";
import { adminApi } from "./adminApi";

export default function AdminPassword({ onUnauthorized, showToast }) {
 const [current, setCurrent] = useState("");
 const [next, setNext] = useState("");
 const [confirm, setConfirm] = useState("");
 const [saving, setSaving] = useState(false);
 const [savedAt, setSavedAt] = useState(0);
 const [err, setErr] = useState("");

 const submit = async (e) => {
  e.preventDefault();
  setErr("");
  if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
  if (next !== confirm) { setErr("New password and confirmation don't match."); return; }
  setSaving(true);
  try {
   await adminApi("/admin/change-password", { method: "POST", body: { current_password: current, new_password: next } });
   setSavedAt(Date.now());
   setTimeout(() => setSavedAt(0), 4000);
   setCurrent(""); setNext(""); setConfirm("");
   showToast("Password updated.");
  } catch (e) {
   // 401 from this endpoint only happens if the JWT is missing/expired ,
   // wrong current password returns 400. Don't blanket-logout on any 401 here.
   if (e.status === 401 && /token|bearer|expired|invalid token/i.test(e.message || "")) {
    onUnauthorized(); return;
   }
   setErr(e.message || "Update failed");
  } finally { setSaving(false); }
 };

 return (
  <div className="max-w-xl" data-testid="admin-tab-password">
   <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-2">
    <KeyRound className="w-3.5 h-3.5" /> Change Admin Password
   </div>
   <p className="text-white/65 text-sm mb-6">Replaces the default password from the environment file. Min 8 characters. Your active session stays signed in.</p>

   <form onSubmit={submit} className="space-y-5" data-testid="admin-password-form">
    <Field label="Current password" value={current} setValue={setCurrent} testid="admin-pw-current" />
    <Field label="New password" value={next} setValue={setNext} testid="admin-pw-new" />
    <Field label="Confirm new password" value={confirm} setValue={setConfirm} testid="admin-pw-confirm" />

    {err && <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" data-testid="admin-pw-error">{err}</div>}
    {savedAt ? (
     <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 inline-flex items-center gap-2" data-testid="admin-pw-saved">
      <Check className="w-4 h-4" /> Password updated.
     </div>
    ) : null}

    <button
     type="submit"
     disabled={saving || !current || !next || !confirm}
     className="px-6 py-3 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#0A0A0A] font-black text-xs tracking-[0.22em] uppercase disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.01] transition-transform"
     data-testid="admin-pw-submit"
    >
     {saving ? "Updating…" : "Update password"}
    </button>
   </form>
  </div>
 );
}

function Field({ label, value, setValue, testid }) {
 const [show, setShow] = useState(false);
 return (
  <label className="block">
   <span className="block text-[10px] uppercase tracking-[0.28em] font-bold text-white/55 mb-2">{label}</span>
   <div className="relative">
    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
    <input
     type={show ? "text" : "password"}
     value={value}
     onChange={(e) => setValue(e.target.value)}
     className="w-full bg-white/[0.04] border border-white/15 rounded-2xl pl-11 pr-14 py-3.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-amber-400/60 focus:bg-white/[0.06] transition-colors"
     data-testid={testid}
     autoComplete="new-password"
    />
    <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 hover:text-white px-2 py-1">
     {show ? "Hide" : "Show"}
    </button>
   </div>
  </label>
 );
}
